import type { IAuthorization } from '@frontend/components/interfaces';
import type { IRequestAddEdit } from '@frontend/components/pages/requests/RequestItemAddEdit';
import type { ServiceCode } from '../../../../common/enums/serviceCode';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import { ServiceType } from '../../../../common/enums/serviceType';
import { ToastType } from '../../../../common/enums/toastType';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { showServiceToast } from '../../../utils/showToast';
import { ReloadEpisode } from '../../pages/episodes/reloadEpisode';
import { Button } from '../common';
import { Component } from '../Component';
import { ConfirmationModal } from '../modals/ConfirmationModal';

interface ICancelProcessButtonProps {
    authorization?: IAuthorization;
    cancelProcess: () => Promise<void>;
    isAutoSaveModalVisible?: boolean;
    onShowAutoSaveModal?: (showModal: boolean) => void;
    reloadProcess?: (mode?: ReloadEpisode) => Promise<void>;
    request: IRequestGetOne | IRequestAddEdit;
    serviceCode: ServiceCode;
}

export class CancelProcessButton extends Component<
    ICancelProcessButtonProps,
    { authorization?: IAuthorization; showModal?: boolean }
> {
    public constructor(props: ICancelProcessButtonProps) {
        super(props);
        this.state = {
            authorization: 'authorization' in props.request ? props.request.authorization : props.authorization,
        };
    }
    public render() {
        if (!this.state.authorization || !this.state.authorization.canUpdateRequestToCancelled) {
            return null;
        }

        return (
            <>
                <ConfirmationModal
                    handleNo={async () => this.handleSetShowModal(false)}
                    handleYes={async () => await this.cancelProcess()}
                    isOpen={this.state.showModal || this.props.isAutoSaveModalVisible || false}
                    question={this.getLabel()}
                    title={translateKey('common.notice')}
                />
                <Button
                    color="secondary"
                    large
                    onClick={async () => this.handleSetShowModal(true)}
                    outlined
                >
                    {translateKey('request.statusAction_cancelled')}
                </Button>
            </>
        );
    }

    private getLabel() {
        switch ('service' in this.props.request ? this.props.request.service.serviceType : undefined) {
            case ServiceType.contact:
                return translateKey('request.confirmCancelContact');
            case ServiceType.referral:
            case ServiceType.referralSmall:
                return translateKey('request.confirmCancelReferral');
            case ServiceType.counseling:
                return translateKey('request.confirmCancelCounseling');
            default:
                return translateKey('request.confirmCancelRequest');
        }
    }

    private async cancelProcess() {
        await fetchJson(`/api/requests/${this.props.request.id}/cancel`, 'POST', {
            reasonStopProccess: 'Cancelled by practitioner',
        });

        showServiceToast(
            ToastType.info,
            'toast.cancelSuccess',
            { code: this.props.serviceCode },
            'request',
            translateKey('toast.cancelDefaultMessage')
        );
        await this.props.cancelProcess();

        if (
            'serviceType' in this.props.request &&
            [ServiceType.referral, ServiceType.referralSmall].includes(this.props.request.serviceType) &&
            this.props.request.previousRequestId
        ) {
            const result = await fetchJson(`/api/requests/${this.props.request.previousRequestId}`, 'GET');
            if (
                result.json.serviceType === ServiceType.referral ||
                result.json.serviceType === ServiceType.referralSmall
            ) {
                await fetchJson(`/api/requests/${this.props.request.previousRequestId}/rejectAndArchive`, 'POST');
                if (this.props.reloadProcess) {
                    await this.props.reloadProcess(ReloadEpisode.reloadAll);
                }
            }
        }
    }

    private async handleSetShowModal(isVisible: boolean) {
        if (this.props.onShowAutoSaveModal) {
            this.props.onShowAutoSaveModal(isVisible);
        } else {
            await this.setState({ showModal: isVisible });
        }
    }
}
